export const DEFAULT_SEAT_SPACING = 40; // px, "our own seating chart scale"
export const DEFAULT_ROW_SPACING = 45; // px

let _idCounter = 0;
export function nextTempId(prefix = "seat") {
  _idCounter += 1;
  return `${prefix}-${Date.now()}-${_idCounter}`;
}

export const distance = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
export const angleDeg = (a, b) => (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
const toRad = (deg) => (deg * Math.PI) / 180;

/**
 * Angle snapping with three states, matching the on-canvas hint text:
 *  - default: "soft" snap — if you're within `softThreshold` degrees of a
 *    15° increment, snap to it (keeps rows straight without forcing it)
 *  - Shift held: "hard" snap — always snap to the nearest 15°, regardless
 *    of how far off you are
 *  - Alt held: no snapping at all, use the raw angle
 */
export function resolveAngle(rawAngle, { shift = false, alt = false, step = 15, softThreshold = 5 } = {}) {
  if (alt) return { angle: rawAngle, snapped: false };
  const nearest = Math.round(rawAngle / step) * step;
  const diff = Math.abs(((rawAngle - nearest + 540) % 360) - 180); // shortest angular distance
  if (shift || diff <= softThreshold) {
    return { angle: nearest, snapped: true };
  }
  return { angle: rawAngle, snapped: false };
}

/** Which guide line (if any) to draw for a given snapped angle. */
export function guideOrientationForAngle(angle) {
  const norm = ((angle % 180) + 180) % 180;
  if (norm === 0) return "horizontal";
  if (norm === 90) return "vertical";
  return null;
}

export function incrementRowLabel(label, steps = 1) {
  if (steps === 0) return label;
  if (/^\d+$/.test(label)) return String(parseInt(label, 10) + steps);
  if (/^[A-Za-z]+$/.test(label)) {
    const isLower = label === label.toLowerCase();
    let n =
      label
        .toUpperCase()
        .split("")
        .reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0) + steps;
    let out = "";
    while (n > 0) {
      const rem = (n - 1) % 26;
      out = String.fromCharCode(65 + rem) + out;
      n = Math.floor((n - 1) / 26);
    }
    return isLower ? out.toLowerCase() : out;
  }
  return `${label}-${steps}`;
}

export function buildRowGroupDescriptor({
  rowGroupId = nextTempId("rowgroup"),
  anchor,
  angle,
  seatsPerRow,
  seatSpacing = DEFAULT_SEAT_SPACING,
  rowCount = 1,
  rowSpacing = DEFAULT_ROW_SPACING,
  perpAngle = null,
  startRowLabel = "1",
  startSeatNumber = 1,
  sectionId = null,
  categoryId = null,
  reverseSeatOrder = false,
  reverseRowOrder = false,
}) {
  return {
    rowGroupId,
    anchor,
    angle,
    seatsPerRow: Math.max(1, seatsPerRow),
    seatSpacing,
    rowCount: Math.max(1, rowCount),
    rowSpacing,
    perpAngle: perpAngle ?? angle + 90,
    startRowLabel,
    startSeatNumber,
    sectionId,
    categoryId,
    reverseSeatOrder, 
    reverseRowOrder
  };
}

/**
 * Turns a row-group descriptor into a flat array of seat objects. Existing
 * seats are matched positionally so edits move seats in place rather than
 * delete + recreate; every seat — new or reused — always ends up with a
 * concrete id.
 */
export function generateSeatsFromRowGroup(descriptor, existingSeats = []) {
  const { rowGroupId, anchor, angle, seatsPerRow, seatSpacing, rowCount, rowSpacing, perpAngle, startRowLabel, startSeatNumber, sectionId, categoryId, reverseSeatOrder, reverseRowOrder } = descriptor;

  const dir = { x: Math.cos(toRad(angle)), y: Math.sin(toRad(angle)) };
  const perp = { x: Math.cos(toRad(perpAngle)), y: Math.sin(toRad(perpAngle)) };

  // key existing seats by their stable (row, column) position within the
  // group, not by array order — array order isn't guaranteed stable across
  // a DB round-trip once any seat in the group has been updated
  const existingByPosition = new Map();
  for (const seat of existingSeats) {
    const key = seat.metadata?.positionKey;
    if (key != null) existingByPosition.set(key, seat);
  }

  const seats = [];
  for (let r = 0; r < rowCount; r++) {
    const rowIndex = reverseRowOrder ? rowCount - 1 - r : r;
    const rowLabel = incrementRowLabel(startRowLabel, rowIndex);
    const rowOrigin = { x: anchor.x + perp.x * rowSpacing * r, y: anchor.y + perp.y * rowSpacing * r };

    for (let i = 0; i < seatsPerRow; i++) {
      const seatIndex = reverseSeatOrder ? seatsPerRow - 1 - i : i;
      const positionKey = `${r}-${i}`;
      const pos = { x: rowOrigin.x + dir.x * seatSpacing * i, y: rowOrigin.y + dir.y * seatSpacing * i };
      const reuse = existingByPosition.get(positionKey);

      seats.push({
        id: reuse?.id ?? nextTempId("seat"),
        sectionId,
        categoryId: reuse?.categoryId ?? categoryId,
        rowLabel,
        label: String(startSeatNumber + seatIndex),
        x: pos.x,
        y: pos.y,
        rotation: angle,
        metadata: { rowGroup: { ...descriptor, rowGroupId }, positionKey },
      });
    }
  }
  return seats;
}

export function previewRow(start, current, { seatSpacing = DEFAULT_SEAT_SPACING, shift, alt, sectionId = null, categoryId = null, startRowLabel = "1"  } = {}) {
  const rawAngle = angleDeg(start, current);
  const { angle } = resolveAngle(rawAngle, { shift, alt });
  const len = distance(start, current);
  const seatsPerRow = Math.max(1, Math.round(len / seatSpacing) + 1);

  const descriptor = buildRowGroupDescriptor({ anchor: start, angle, seatsPerRow, seatSpacing, rowCount: 1, sectionId, categoryId, startRowLabel });
  return { descriptor, seats: generateSeatsFromRowGroup(descriptor), rawAngle, snappedAngle: angle };
}

export function previewGrid(anchor, current, { seatSpacing = DEFAULT_SEAT_SPACING, rowSpacing = DEFAULT_ROW_SPACING, reverseSeatOrder, reverseRowOrder, startRowLabel = "1"  } = {}) {
  const dx = current.x - anchor.x;
  const dy = current.y - anchor.y;
  const seatsPerRow = Math.max(1, Math.round(Math.abs(dx) / seatSpacing) + 1);
  const rowCount = Math.max(1, Math.round(Math.abs(dy) / rowSpacing) + 1);
  const angle = dx < 0 ? 180 : 0;
  const perpAngle = dy < 0 ? -90 : 90;

  const rows = generateRowsFromGrid({
    anchor: { x: anchor.x, y: anchor.y }, angle, perpAngle, seatsPerRow, seatSpacing, rowCount, rowSpacing,
    reverseSeatOrder, reverseRowOrder, startRowLabel
  });

  // kept flat + with seatsPerRow/rowCount so the existing live-preview
  // rendering in CanvasStage (ghost seats, "R x C" label) needs zero changes
  return { rows, seats: rows.flatMap((row) => row.seats), seatsPerRow, rowCount };
}

export function snapPointToSeats(point, seats, threshold = 6) {
  let snapped = { ...point };
  let guideX = null;
  let guideY = null;

  for (const seat of seats) {
    if (guideX === null && Math.abs(seat.x - point.x) <= threshold) {
      snapped = { ...snapped, x: seat.x };
      guideX = seat.x;
    }
    if (guideY === null && Math.abs(seat.y - point.y) <= threshold) {
      snapped = { ...snapped, y: seat.y };
      guideY = seat.y;
    }
  }

  return { point: snapped, guideX, guideY };
}

// canvasSeatGeometryUtils.js
export function generateRowsFromGrid({
  anchor, angle, perpAngle, seatsPerRow, seatSpacing, rowCount, rowSpacing,
  startRowLabel = "1", startSeatNumber = 1, sectionId = null, categoryId = null,
  reverseSeatOrder = false, reverseRowOrder = false,
}) {
  const perp = { x: Math.cos(toRad(perpAngle)), y: Math.sin(toRad(perpAngle)) };
  const rows = [];

  for (let r = 0; r < rowCount; r++) {
    const rowIndex = reverseRowOrder ? rowCount - 1 - r : r;
    const rowOrigin = { x: anchor.x + perp.x * rowSpacing * r, y: anchor.y + perp.y * rowSpacing * r };
    const descriptor = buildRowGroupDescriptor({
      anchor: rowOrigin,
      angle,
      seatsPerRow,
      seatSpacing,
      rowCount: 1,
      startRowLabel: incrementRowLabel(startRowLabel, rowIndex),
      startSeatNumber,
      sectionId,
      categoryId,
      reverseSeatOrder,
    });
    rows.push({ descriptor, seats: generateSeatsFromRowGroup(descriptor) });
  }
  return rows;
}